import { gql } from '@apollo/client';
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ExitIcon from '@moda/icons/exit-24';
import NextIcon from '@moda/icons/next-16';
import PrevIcon from '@moda/icons/prev-16';
import { Clickable } from '@moda/om';
import { compact } from '@moda/portal-stanchions';
import { Link } from '../../../components/Link';
import { hrefFor } from '../../../routers';
import { MobileLookviewPageFragment } from '../../../generated/types';
import {
  MobileLookviewDetailView,
  MOBILE_LOOKVIEW_DETAIL_VIEW_FRAGMENT
} from './MobileLookviewDetailView';

import './MobileLookviewPage.scss';

export const MOBILE_LOOKVIEW_PAGE_FRAGMENT = gql`
  fragment MobileLookviewPageFragment on Look {
    id
    primaryImageUrls: primary_image_urls {
      src: thumb
    }
    ...MobileLookviewDetailViewFragment
  }
  ${MOBILE_LOOKVIEW_DETAIL_VIEW_FRAGMENT}
`;

interface Props {
  looks: MobileLookviewPageFragment[];
}

const getIndexFromLookId = (looks: MobileLookviewPageFragment[], id: string): number => {
  return looks.findIndex((look: MobileLookviewPageFragment) => {
    return look.id === id;
  });
};

export const MobileLookviewPage: React.FC<Props> = ({ looks }) => {
  const {
    lookId = '0',
    vertical = 'women',
    trunkshowSlug = '',
    trunkshowId = ''
  } = useParams<{
    lookId?: string;
    vertical?: string;
    trunkshowSlug?: string;
    trunkshowId?: string;
  }>();

  const [activeLookIndex, setActiveLookIndex] = useState(getIndexFromLookId(looks, lookId));

  const history = useHistory();
  const location = useLocation();

  const handleClickNext = useCallback(() => {
    if (activeLookIndex < looks.length - 1) {
      setActiveLookIndex(activeLookIndex + 1);
    }
  }, [activeLookIndex, looks]);

  const handleClickPrev = useCallback(() => {
    if (activeLookIndex >= 1) {
      setActiveLookIndex(activeLookIndex - 1);
    }
  }, [activeLookIndex]);

  const handleSetActiveLookIndex = useCallback((index: number) => setActiveLookIndex(index), []);

  useEffect(() => {
    if (lookId != looks[activeLookIndex].id) {
      history.push(`${looks[activeLookIndex].id}`);
    }
  }, [activeLookIndex, history, lookId, looks]);

  const lookImages = useMemo(
    () =>
      compact(compact(looks.map(({ primaryImageUrls }) => primaryImageUrls)).map(({ src }) => src)),
    [looks]
  );

  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  return (
    <div className="MobileLookviewPage">
      <div className="MobileLookviewPage__actions">
        <Link
          className="MobileLookviewPage__exit"
          to={
            returnTo
              ? `/${returnTo}`
              : hrefFor.TrunkshowPage({
                  vertical: vertical,
                  trunkshowSlug: trunkshowSlug,
                  trunkshowId: trunkshowId
                })
          }
        >
          <ExitIcon />
        </Link>

        <nav className="MobileLookviewPage__nav">
          <Clickable className="MobileLookviewPage__icon" onClick={handleClickPrev}>
            <PrevIcon>Previous</PrevIcon>
          </Clickable>

          <span>
            Look {activeLookIndex + 1} / {looks.length}
          </span>

          <Clickable className="MobileLookviewPage__icon" onClick={handleClickNext}>
            <NextIcon>Next</NextIcon>
          </Clickable>
        </nav>
      </div>

      <MobileLookviewDetailView
        look={looks[activeLookIndex]}
        lookImages={lookImages}
        activeLookIndex={activeLookIndex}
        onSetActiveLookIndex={handleSetActiveLookIndex}
      />
    </div>
  );
};
